import { supabase } from "@/lib/supabase";
import type { AdsActivityFreshAchievement } from "@/lib/adsAssistantClientEngagement";
import { readWorkspaceOrgIdFromStorage } from "@/lib/workspaceOrgStorage";

export const LEADS_ACHIEVEMENT_CHECK_PATH = "/api/leads/achievement-check";

export type LeadAchievementCheckResponse =
  | { ok: true; fresh_achievements: AdsActivityFreshAchievement[]; skipped?: string }
  | { ok: false; error: string };

/** Idempotent server check after a lead upsert — awards first-logged + count milestones. */
export async function runLeadAchievementCheck(): Promise<LeadAchievementCheckResponse> {
  if (!supabase) return { ok: false, error: "App is not configured for sign-in." };

  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, error: "Sign in required." };

  const workspaceOrgId = readWorkspaceOrgIdFromStorage();

  try {
    const r = await fetch(LEADS_ACHIEVEMENT_CHECK_PATH, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...(workspaceOrgId ? { "X-Workspace-Organization-Id": workspaceOrgId } : {}),
      },
      body: "{}",
    });
    const text = await r.text();
    let json: unknown = null;
    try {
      json = JSON.parse(text);
    } catch {
      /* non-JSON */
    }

    if (!r.ok) {
      const err =
        json && typeof json === "object" && json !== null && "error" in json &&
        typeof (json as { error: unknown }).error === "string"
          ? (json as { error: string }).error
          : text || `HTTP ${r.status}`;
      return { ok: false, error: err };
    }

    return json as LeadAchievementCheckResponse;
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}
